import ENDPOINTS from '@/config/http/endpoints'
import { parseResponse } from '@/config/http/http.helpers'
import { ApiFetchBankInformationSchema } from '@/ducks/billing/billing.schema'
import { normalizeWalletDetailsResponse } from '@/ducks/billing/billing.wallet-details'
import { normalizeWalletTransactionDetailsResponse } from '@/ducks/billing/billing.wallet-transaction-details'
import {
  ApiFetchBankInformation,
  IGetBillingCycleDetailsResponse,
  IGetBillingCyclesResponse,
  IGetWalletTransactionDetailsResponse,
  IGetWalletTransactionsResponse,
  WalletDetailsApiResponse,
  WalletDetailsViewModel,
  WalletTransactionDetailsViewModel
} from '@/ducks/billing/billing.types'
import { rootApi } from '@/ducks/root.api'

export const billingApi = rootApi.injectEndpoints({
  endpoints: build => {
    return {
      fetchBillingCycles: build.query<
        IGetBillingCyclesResponse,
        { storeId: string }
      >({
        query: ({ storeId }) => ({
          method: 'GET',
          url: ENDPOINTS.GET_BILLING_CYCLES(storeId)
        })
      }),
      fetchBillingCycleDetails: build.query<
        IGetBillingCycleDetailsResponse,
        { storeId: string; cycleId: string }
      >({
        query: ({ storeId, cycleId }) => ({
          method: 'GET',
          url: ENDPOINTS.GET_BILLING_CYCLE_DETAILS(storeId, cycleId)
        })
      }),
      fetchBankInformation: build.query<
        ApiFetchBankInformation,
        { storeId: string }
      >({
        query: ({ storeId }) => ({
          method: 'GET',
          url: ENDPOINTS.GET_BANK_INFORMATION(storeId)
        }),
        transformResponse: (response: ApiFetchBankInformation) => {
          return parseResponse(response, ApiFetchBankInformationSchema)
        }
      }),
      fetchWalletTransactions: build.query<
        IGetWalletTransactionsResponse,
        { limit?: number; offset?: number; brand?: string }
      >({
        query: ({ limit = 20, offset = 0, brand }) => ({
          method: 'GET',
          url: ENDPOINTS.GET_WALLET_TRANSACTIONS,
          params: { limit, offset, ...(brand ? { brand } : {}) }
        })
      }),
      fetchWalletDetails: build.query<
        WalletDetailsViewModel,
        { token?: string } | void
      >({
        query: args => ({
          method: 'GET',
          url: ENDPOINTS.GET_WALLET_DETAILS,
          headers: args?.token
            ? {
                Authorization: args.token
              }
            : undefined
        }),
        transformResponse: (response: WalletDetailsApiResponse) => {
          if (!response || response.status !== '1') {
            throw new Error(response?.message || 'Failed to fetch wallet details')
          }

          return normalizeWalletDetailsResponse(response)
        }
      }),
      fetchWalletTransactionDetails: build.query<
        WalletTransactionDetailsViewModel,
        { transactionId: string }
      >({
        query: ({ transactionId }) => ({
          method: 'GET',
          url: ENDPOINTS.GET_WALLET_TRANSACTION_DETAILS(transactionId)
        }),
        transformResponse: (response: IGetWalletTransactionDetailsResponse) => {
          if (!response || response.status !== '1') {
            throw new Error(
              response?.message || 'Failed to fetch wallet transaction details'
            )
          }

          return normalizeWalletTransactionDetailsResponse(response)
        }
      })
    }
  }
})

const {
  useLazyFetchBillingCyclesQuery,
  useLazyFetchBillingCycleDetailsQuery,
  useFetchBankInformationQuery,
  useLazyFetchWalletTransactionsQuery,
  useLazyFetchWalletDetailsQuery,
  useLazyFetchWalletTransactionDetailsQuery
} = billingApi

export {
  useFetchBankInformationQuery,
  useLazyFetchBillingCycleDetailsQuery,
  useLazyFetchBillingCyclesQuery,
  useLazyFetchWalletDetailsQuery,
  useLazyFetchWalletTransactionDetailsQuery,
  useLazyFetchWalletTransactionsQuery
}
